/**
 * Copy an array
 *
 * @param array
 * @return {any}
 */
export const copyArray = (array) => {
    return JSON.parse(JSON.stringify(array))
};

/**
 * Sort array by object keys and values
 *
 * @param key
 * @param order
 * @return {Function}
 */
export const compareValues = (key, order = 'asc') => {
    return function (a, b) {
        if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
            return 0;
        }
        const varA = (typeof a[key] === 'string') ? a[key].toUpperCase() : a[key];
        const varB = (typeof b[key] === 'string') ? b[key].toUpperCase() : b[key];
        let comparison = 0;
        if (varA > varB) {
            comparison = 1;
        } else if (varA < varB) {
            comparison = -1;
        }
        return (
            (order === 'desc') ? (comparison * -1) : comparison
        );
    };
};

/**
 * Sort array of objects alphanumerically
 *
 * @param arr
 * @param sortField
 * @param sortOrder
 */
export const sortArrayByKey = (arr, sortField, sortOrder = 'ASC') => {
    arr.sort(function (a, b) {
        return a[sortField].toString().toLowerCase().localeCompare(b[sortField].toString().toLowerCase());
    });
    if (sortOrder === 'DESC') {
        arr = arr.reverse();
    }
    return arr;
};

/*
 * Format a new datetime object
 */
export const createDateTimeObject = () => {
    // Format: YYYY-MM-DD HH:MM:SS
    const currentDate = new Date();
    return currentDate.getFullYear() + '-'
        + padDateString(currentDate.getMonth() + 1) + '-'
        + padDateString(currentDate.getDate()) + ' '
        + padDateString(currentDate.getHours()) + ':'
        + padDateString(currentDate.getMinutes()) + ':'
        + padDateString(currentDate.getSeconds());
};

export const createDateTimeObjectFromDate = (datetime) => {
    // Format: YYYY-MM-DD HH:MM:SS
    const dateObj = new Date(datetime);
    if (!dateObj) {
        return '';
    }
    return dateObj.getFullYear() + '-'
        + padDateString(dateObj.getMonth() + 1) + '-'
        + padDateString(dateObj.getDate()) + ' '
        + padDateString(dateObj.getHours()) + ':'
        + padDateString(dateObj.getMinutes()) + ':'
        + padDateString(dateObj.getSeconds());
};

/**
 * Pad numbers less than 10 with a leading zero
 *
 * @param num
 * @return {string}
 */
export const padDateString = (num) => {
    return String('00' + num).slice(-2);
};

/**
 * Strip HTML and script tags from a string
 *
 * @param str
 * @return {*}
 */
export const stripTags = (str) => {
    return str.replace(/(<([^>]+)>)/ig,"");
};

/**
 * Add commas to numerical strings
 *
 * @param nStr
 * @return {string}
 */
export const addCommas = nStr => {
    nStr += '';
    let x = nStr.split('.');
    let x1 = x[0];
    let x2 = x.length > 1 ? '.' + x[1] : '';
    let rgx = /(\d+)(\d{3})/;
    while (rgx.test(x1)) {
        x1 = x1.replace(rgx, '$1,$2');
    }
    return x1 + x2;
};

/**
 * Convert retrieved Date object into separate fields (for Discounts)
 */
export const convertDateToFields = (date) => {
    let h = date.getHours();
    let ampm = 'am';
    if (h === 12) {
        h = 0;
        ampm = 'pm';
    }
    if (h > 12) {
        h -= 12;
        ampm = 'pm';
    }
    h = String('00' + h).slice(-2);
    let m = date.getMinutes();
    m = String('00' + m).slice(-2);

    return {hour: String(h), min: String(m), ampm: ampm};
};


/**
 * Convert timestamp to display date with optional time
 *
 * @param datetime
 * @param includeTime
 * @return {string}
 */
export const convertDate = (datetime, includeTime = true) => {
    const dateObj = new Date(datetime);
    const mo = dateObj.getMonth() + 1;
    const dy = dateObj.getDate();
    const yy = dateObj.getFullYear();
    let timeStr = '';
    if (includeTime) {
        let hr = dateObj.getHours();
        const ampm = (hr > 11) ? 'pm' : 'am';
        if (hr === 0) {
            hr = 12;
        }
        if (hr > 12) {
            hr -= 12;
        }
        let mn = dateObj.getMinutes();
        let minVal = String('00' + mn).slice(-2);
        timeStr = ' ' + hr + ':' + minVal + ' ' + ampm;
    }
    let dateStr = mo + '/' + dy + '/' + yy + timeStr;
    if (isNaN(yy) || isNaN(mo)) {
        dateStr = 'No Date';
    }
    return dateStr;
};

/**
 * Convert Date to a readable string with local time zone
 *
 * @param datetime
 * @param includeTz
 * @returns {string}
 */
export const convertDateWithLocalTimeZone = (datetime, includeTz = true) => {
    const dateObj = new Date(datetime);
    const yy = dateObj.getFullYear();
    const mo = dateObj.getMonth() + 1;
    const dy = dateObj.getDate();
    let timeStr = '';
    let hr = dateObj.getHours();
    const ampm = (hr > 11) ? 'PM' : 'AM';
    if (hr === 0) {
        hr = 12;
    }
    if (hr > 12) {
        hr -= 12;
    }
    let minVal = padDateString(dateObj.getMinutes());
    timeStr = ' ' + hr + ':' + minVal + ' ' + ampm;
    let dateStr = yy + '-' + padDateString(mo) + '-' + padDateString(dy) + timeStr;
    if (includeTz) {
        dateStr += ' ' + getLocalTimeZoneName();
    }
    if (isNaN(yy) || isNaN(mo)) {
        dateStr = 'No Date';
    }
    return dateStr;
};

export const convertDateToLocalString = (datetime, includeTimezone = true) => {
    const date = new Date(datetime);
    let str = date.toLocaleString();
    if (includeTimezone) {
        str += ' ' + getLocalTimeZoneName();
    }
    return str;
};

/**
 * Convert timestamp to Zero Day display
 *
 * @param datetime
 * @returns {string}
 */
export const convertDateToZeroDay = (datetime) => {
    const dateObj = new Date(datetime);
    const mo = dateObj.getMonth() + 1;
    const dy = dateObj.getDate();
    const yy = dateObj.getFullYear();
    let dateStr = yy + padDateString(mo) + padDateString(dy);
    if (isNaN(yy) || isNaN(mo)) {
        dateStr = 'No Date';
    }
    return dateStr;
};

export const getLocalTimeZoneName = () => {
    return new Date().toLocaleDateString('en-US', {timeZoneName: 'short'}).slice(-3);
};

/**
 * Generate a UUID for payloads and unique keys (per React's unique-key requirement)
 */
export const generateUUID = () => {
    let dt = new Date().getTime();
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (dt + Math.random() * 16) % 16 | 0;
        dt = Math.floor(dt / 16);
        return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
    });
    return uuid;
};

/**
 * Create GET headers
 *
 * @return Object
 */
export const getGetHeaders = () => {
    const headers = {'Cache-Control': 'no-cache'};
    const token = localStorage.getItem('token');
    headers.Authorization = 'Bearer ' + token;
    return headers;
};


export const extractDiscountScope = (scope, prefix) => {
    const pattern = new RegExp(`${prefix}_([a-zA-Z]+)\\s*=\\s*([\\w,&]+)(?:\\s+(OR|AND)\\s+|(?=\\s*|$))`, 'g');


    const matches = [...scope.matchAll(pattern)];
    return matches.map((match, index) => {
        const [, type, values, operator] = match;
        const separator = values.includes(",") ? "comma" : values.includes("&") ? "ampersand" : "none";
        const valuesArray = separator === "comma" ? values.split(",") : values.split("&");
        const logicalOperator = separator === "comma" ? "OR" : separator === "ampersand" ? "AND" : null;

        return {
            type: type,
            values: valuesArray,
            separator: separator,
            logicalOperator: operator || logicalOperator
        };

    });
}

export const extractDiscountItemCondition = (itemCondition) => {
    if (!itemCondition) {
        return {};
    }

    const pattern = /(\w+)\s*([!=]+)\s*([\d,]+)/g;
    const result = {};

    let match;
    do {
        match = pattern.exec(itemCondition);
        if (match) {
            const [, key, operator, values] = match;
            const valueArray = values.split(',').map(value => value.trim());

            result[key] = {
                values: valueArray,
                comparison: operator === '=' ? 'equals' : operator === '!=' ? 'notequals' : null
            };
        }
    } while (match);

    return result;
};


export const removeExtraSpaces = (str) => {
    return str.replace(/\s+/g, ' ').trim();
}


// WEBPACK FOOTER //
// ./src/Fuse/shared/functions.js